<script setup lang="ts">
import type { LocationQuery } from 'vue-router';
import type {
  // InitialData as TomorrowReportSearchFormInitialData, // TODO need to refactor :initial-data="route.query"
  ValidData as TomorrowReportSearchFormValidData,
} from '@/components/searchForms/TomorrowReportSearchForm.vue';

import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import _isEqual from 'lodash/isEqual';

import TableFooterBar from '@/components/bars/TableFooterBar.vue';
import KBtn from '@/components/btns/KBtn.vue';
import TomorrowReportSearchForm from '@/components/searchForms/TomorrowReportSearchForm.vue';
import TomorrowReportTable from '@/components/tables/TomorrowReportTable.vue';
import TomorrowToBeScheduledReportTable from '@/components/tables/TomorrowToBeScheduledReportTable.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import {
  fetchGetTomorrowScheduledReport,
  fetchGetTomorrowScheduledReportFile,
  fetchGetTomorrowSplitToBaseReport,
  fetchGetTomorrowSplitToBaseReportFile,
  fetchGetTomorrowToBeScheduledReport,
  fetchGetTomorrowToBeScheduledReportFile,
} from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';
import {
  TomorrowReportData,
  TomorrowTobeSchedReportData,
} from '@/models/reports';

export interface TomorrowReportsTables {
  scheduled: TomorrowReportData[];
  toBeScheduled: TomorrowTobeSchedReportData[];
  scheduledSplitToBase: TomorrowReportData[];
}

const isFetchingScheduledData = ref(false);
const isFetchingToBeScheduledData = ref(false);
const isFetchingSplitToBaseData = ref(false);

const isDownloadingScheduled = ref(false);
const isDownloadingToBeScheduled = ref(false);
const isDownloadingSplitToBase = ref(false);

const tomorrowReportData = ref<TomorrowReportsTables>({
  scheduled: [],
  toBeScheduled: [],
  scheduledSplitToBase: [],
});

const router = useRouter();
const route = useRoute();

function handleSubmit(formData: TomorrowReportSearchFormValidData): void {
  const query = { ...route.query, ...formData };
  const isReplace = _isEqual(route.query, query);
  void router.push({
    name: routeNames.TOMORROW_REPORT,
    query,
    force: true,
    replace: isReplace,
  });
}

async function fetchData(newQuery: LocationQuery): Promise<void> {
  const { division, date } = newQuery;
  if (!division || !date) {
    tomorrowReportData.value.scheduled = [];
    return;
  }
  isFetchingScheduledData.value = true;
  isFetchingToBeScheduledData.value = true;
  isFetchingSplitToBaseData.value = true;

  // TODO
  // @ts-expect-error
  void fetchGetTomorrowScheduledReport({ date, division })
    .then((data) => {
      if (!_isEqual(route.query, newQuery)) {
        return;
      }
      tomorrowReportData.value.scheduled = data;
    })
    .finally(() => {
      isFetchingScheduledData.value = false;
    });

  // TODO
  // @ts-expect-error
  void fetchGetTomorrowToBeScheduledReport({ date, division })
    .then((data) => {
      if (!_isEqual(route.query, newQuery)) {
        return;
      }
      tomorrowReportData.value.toBeScheduled = data;
    })
    .finally(() => {
      isFetchingToBeScheduledData.value = false;
    });

  // TODO
  // @ts-expect-error
  void fetchGetTomorrowSplitToBaseReport({ date, division })
    .then((data) => {
      if (!_isEqual(route.query, newQuery)) {
        return;
      }
      tomorrowReportData.value.scheduledSplitToBase = data;
    })
    .finally(() => {
      isFetchingSplitToBaseData.value = false;
    });
}

watch(
  route,
  async (newRoute) => {
    if (newRoute.name === routeNames.TOMORROW_REPORT) {
      await fetchData(newRoute.query);
    }
  },
  { immediate: true },
);

function handleDownloadScheduledReport(): void {
  const { division, date } = route.query;
  if (!division || !date) {
    return;
  }
  isDownloadingScheduled.value = true;
  // TODO
  // @ts-expect-error
  void fetchGetTomorrowScheduledReportFile({ date, division }).finally(() => {
    isDownloadingScheduled.value = false;
  });
}

function handleDownloadToBeScheduledReport(): void {
  const { division, date } = route.query;
  if (!division || !date) {
    return;
  }
  isDownloadingToBeScheduled.value = true;
  // TODO
  // @ts-expect-error
  void fetchGetTomorrowToBeScheduledReportFile({ date, division }).finally(
    () => {
      isDownloadingToBeScheduled.value = false;
    },
  );
}

function handleDownloadSplitToBaseReport(): void {
  const { division, date } = route.query;
  if (!division || !date) {
    return;
  }
  isDownloadingSplitToBase.value = true;
  // TODO
  // @ts-expect-error
  void fetchGetTomorrowSplitToBaseReportFile({ date, division }).finally(() => {
    isDownloadingSplitToBase.value = false;
  });
}
</script>

<template>
  <div :class="$style.tomorrowReportView" data-testid="tomorrowReportView">
    <PageContentWrapper>
      <FormWrapper :class="$style.formWrapper">
        <TomorrowReportSearchForm
          :initial-data="route.query"
          :loading="
            isFetchingScheduledData ||
            isFetchingToBeScheduledData ||
            isFetchingSplitToBaseData
          "
          :class="$style.form"
          @submit="handleSubmit"
        />
      </FormWrapper>
      <div :class="$style.maxWidthWrapper">
        <h3 :class="$style.h3">Scheduled</h3>
        <TomorrowReportTable
          :loading="isFetchingScheduledData"
          :date="route.query.date ? route.query.date.toString() : undefined"
          :data="tomorrowReportData.scheduled"
        />
        <h3 :class="$style.h3">To be Scheduled</h3>
        <TomorrowToBeScheduledReportTable
          :loading="isFetchingToBeScheduledData"
          :date="route.query.date ? route.query.date.toString() : undefined"
          :data="tomorrowReportData.toBeScheduled"
        />
        <h3 :class="$style.h3">Split to Base</h3>
        <TomorrowReportTable
          :loading="isFetchingSplitToBaseData"
          :date="route.query.date ? route.query.date.toString() : undefined"
          :data="tomorrowReportData.scheduledSplitToBase"
          class="mb-16"
        />
      </div>
    </PageContentWrapper>
    <TableFooterBar>
      <KBtn
        size="lg"
        append-icon="mdi-arrow-down"
        :disabled="
          isFetchingScheduledData ||
          isFetchingToBeScheduledData ||
          isFetchingSplitToBaseData ||
          !route.query.division ||
          !route.query.date
        "
        :loading="isDownloadingScheduled"
        @click="handleDownloadScheduledReport"
      >
        Download Scheduled Report
      </KBtn>
      <KBtn
        size="lg"
        append-icon="mdi-arrow-down"
        :disabled="
          isFetchingScheduledData ||
          isFetchingToBeScheduledData ||
          isFetchingSplitToBaseData ||
          !route.query.division ||
          !route.query.date
        "
        :loading="isDownloadingToBeScheduled"
        class="ml-4"
        @click="handleDownloadToBeScheduledReport"
      >
        Download to be Scheduled Report
      </KBtn>
      <KBtn
        size="lg"
        append-icon="mdi-arrow-down"
        :disabled="
          isFetchingScheduledData ||
          isFetchingToBeScheduledData ||
          isFetchingSplitToBaseData ||
          !route.query.division ||
          !route.query.date
        "
        :loading="isDownloadingSplitToBase"
        class="ml-4"
        @click="handleDownloadSplitToBaseReport"
      >
        Download Tomorrow Report
      </KBtn>
    </TableFooterBar>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.tomorrowReportView {
}
  .formWrapper {
    padding: 2.4rem 1.5rem 0.5rem 1.5rem;
    max-width: variables.$tableWrapperWidth;
    margin: 2rem 0;
  }
  .maxWidthWrapper {
    max-width: variables.$tableWrapperWidth;
  }
  .h3 {
    font-weight: 500;
    margin: 1.5rem 0 1.3rem 0;
  }
</style>
